import { BlueButton } from '../../../../customComponents/buttons/BlueButton'
import { AddCircle, CalendarMonth } from '@mui/icons-material'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useState } from 'react'
import { type StartCommunityOfPractice } from '../../../../../models/StartCommunityOfPractice'
import { StartCommunityOfPracticeModal } from './StartCommunityOfPracticeModal'
import { BannerModal } from '../../../../customComponents/BannerModal'
import {
  useStartCommunityOfPracticeModal,
  useStartCommunityOfPractices
} from '../../../../../hooks/use-startCommunityOfPractices'
import { DeleteStartCommunityOfPractice, DeleteStartCommunityOfPracticeTracking, TrackStartCommunityOfPractice } from '../../../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { type ThingsToConsiderSection } from '../ThingsToConsiderCard'
import { BasicTable, type BasicTableColumn } from '../BasicTable'
import { ThingsToConsiderButton } from '../ThingsToConsiderButton'
import { AttendancePlanTrackingModal } from './AttendancePlanTrackingModal'
import { SubBasicTable } from '../SubBasicTable'

interface Props {
  priorityId: number
  annualPlanId: number
  readOnly: boolean
  trackingReadonly: boolean
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase: boolean
  budgetOverviewView?: boolean
}

export const StartCommunityOfPracticeList = (props: Props): JSX.Element => {
  const [showTrackingModal, setShowTrackingModal] = useState(false)
  const [planBeingEdited, setPlanBeingEdited] = useState<StartCommunityOfPractice | null>(null)

  const {
    isLoading,
    startCommunityOfPractices,
    reloadCommunityOfPractices
  } = useStartCommunityOfPractices(props.priorityId, props.implementationPhase)
  const {
    initialValues,
    handleCloseModal,
    showCommunityOfPracticeModal,
    handleAddCommunityOfPractice,
    handleEditCommunityOfPractice
  } = useStartCommunityOfPracticeModal(props.implementationPhase)

  const [communityOfPracticeToDelete, setCommunityOfPracticeToDelete] = useState<StartCommunityOfPractice | null>(null)

  const handleDeleteCommunityOfPractice = async (cop: StartCommunityOfPractice): Promise<void> => {
    const { success, errors } = await DeleteStartCommunityOfPractice(cop.id, cop.isUpdate)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await reloadCommunityOfPractices()
    toast.success('Attendance Plan Deleted')
    setCommunityOfPracticeToDelete(null)
  }

  const handleTrackCommunityOfPractice = async (startCommunityOfPractice: StartCommunityOfPractice): Promise<void> => {
    const dollarAmount: string | undefined = startCommunityOfPractice.actualDollarAmount == null
      ? undefined
      : startCommunityOfPractice.actualDollarAmount?.toString().replace('$', '')
    setPlanBeingEdited({ ...startCommunityOfPractice, actualDollarAmount: dollarAmount })
    setShowTrackingModal(true)
  }

  const deleteTracking = async (startCommunityOfPractice: StartCommunityOfPractice): Promise<void> => {
    await DeleteStartCommunityOfPracticeTracking(startCommunityOfPractice.id)
    await reloadCommunityOfPractices()
    toast.success('Attendance Plan Tracking Deleted')
  }

  const saveTrackingPlanBeingEdited = async (attendees: number, actualBudget: number | null): Promise<void> => {
    if (planBeingEdited != null) {
      const budgetRequestAmount = Number(planBeingEdited.budgetRequest.toString().replace('$', ''))

      const startCommunityOfPractice = {
        ...planBeingEdited,
        expandedContent: undefined,
        budgetRequest: budgetRequestAmount,
        actualAttendeeCount: attendees,
        actualDollarAmount: actualBudget?.toString() ?? ''
      }

      setShowTrackingModal(false)
      const { success, errors } = await TrackStartCommunityOfPractice(startCommunityOfPractice)

      if (!success) {
        errors.forEach(e => toast.error(e))
        return
      }
      await reloadCommunityOfPractices()
      setPlanBeingEdited(null)
      toast.success('Attendance Plan Tracked')
    }
  }

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: '',
      items: [
        'START facilitates CoPs in the areas of Coaching, Comprehensive Communication Supports and Strategies, Early Childhood, Peer to Peer, Practical Functional Assessment and Skill-Based Treatment, Secondary Transition, and Sexual Health.',
        'CoP groups meet live and virtually. Consider budgeting for in-person meetings.',
        <>Visit the <a style={{ color: 'currentcolor' }} target='_blank' href='https://www.gvsu.edu/autismcenter/communities-of-practice-cop-529.htm'>START Communities of Practice (CoP) webpage</a> to review the schedule for next year.</>
      ]
    }
  ]

  const isEmpty = startCommunityOfPractices.filter(cop => !(cop.deleted ?? false)).length === 0
  const columns: BasicTableColumn[] = [
    { id: 'trainingTitle', label: 'Training Title', minWidth: 200 },
    { id: 'numberOfAttendees', label: 'Attendance Count', minWidth: 200 },
    { id: 'budgetRequest', label: 'Budget', minWidth: 100 }
  ]
  const rows = startCommunityOfPractices.filter(cop => !(cop.deleted ?? false)).map(scp => ({
    ...scp,
    isUpdated: scp.isUpdate,
    isTracked: scp.isUpdate && scp.actualAttendeeCount != null,
    trainingTitle: scp.startTraining.title,
    budgetRequest: `$${scp.budgetRequest}`,
    expandedContent: (scp.actualAttendeeCount != null &&
      (
        <Box
          sx={{
            p: '2rem',
            backgroundColor: 'var(--grey-100)'
          }}
        >
          <SubBasicTable
            hasTrackingDetails
            readonly={props.readOnly}
            trackingReadonly={props.trackingReadonly}
            onEditTracking={handleTrackCommunityOfPractice}
            onDeleteTracking={deleteTracking}
            columns={[
              { id: 'actualAttendeeCount', label: 'ACTUAL ATTENDANCE COUNT', minWidth: 200, isTrackingColumn: true },
              { id: 'actualDollarAmount', label: 'ACTUAL DOLLAR AMOUNT', minWidth: 200, isTrackingColumn: true },
              { id: 'trackingAction', label: '', isTrackingColumn: true }
            ]}
            rows={[{
              ...scp,
              id: scp.id,
              actualAttendeeCount: scp.actualAttendeeCount ?? 0,
              actualDollarAmount: `$${scp.actualDollarAmount ?? 0}`
            }]}
            dataTestId={`${scp.id}-expandedTCOPTable`}
          />
        </Box>
      )
    )
  }))

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid='startCommunityOfPracticeList'>
    <Typography variant='h4'>START Communities of Practice</Typography>

    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={handleAddCommunityOfPractice} dataTestId='addCommunityOfPracticeButton'>
            Add Attendance Plan <AddCircle sx={{ ml: '10px' }} fontSize='small' />
          </BlueButton>
        }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='communityOfPracticeThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='communityOfPracticeThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Box sx={{
      border: '1px solid var(--blue-300)',
      backgroundColor: 'var(--white)',
      borderRadius: '8px',
      padding: isEmpty ? '24px' : '0',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0px 10px 10px -8px #3B353E4D'
    }}
    >
      {isLoading
        ? <CircularProgress sx={{ alignSelf: 'center' }} />
        : (isEmpty
            ? <>
              <CalendarMonth sx={{ color: 'var(--blue-300)', width: '144px', height: '144px', alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant='h4'>No Attendance Plan Added</Typography>
                {!props.readOnly &&
                  <Typography variant='body1' fontSize='16px'>
                    Use the Add Attendance Plan button to add a START Communities of Practice Attendance Plan to your Annual Plan.
                  </Typography>
                }
              </Box>
            </>
            : <BasicTable
                dataTestId='startCommunityOfPracticesTable'
                columns={columns}
                indexRowIds={props.implementationPhase}
                rows={rows}
                isLoading={isLoading}
                readonly={props.readOnly}
                trackingReadonly={props.trackingReadonly}
                onEdit={handleEditCommunityOfPractice}
                onUpdate={handleEditCommunityOfPractice}
                onDelete={setCommunityOfPracticeToDelete}
                hideExpand={props.hideExpand}
                implementationPhase={props.implementationPhase}
                onTrack={handleTrackCommunityOfPractice}
                budgetOverviewView={props.budgetOverviewView}
            />
          )
      }

      {showCommunityOfPracticeModal &&
        <StartCommunityOfPracticeModal
          usedIds={startCommunityOfPractices.filter(cop => !(cop.deleted ?? false)).map(r => r.startTraining.id ?? -1)}
          isImplementationPhase={props.implementationPhase}
          open={showCommunityOfPracticeModal}
          priorityId={props.priorityId}
          annualPlanId={props.annualPlanId}
          onClose={handleCloseModal}
          onSaved={reloadCommunityOfPractices}
          initialValues={initialValues}
        />
      }

      {communityOfPracticeToDelete != null &&
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId='confirmDeleteCommunityOfPracticeModal'
          bodyContent={`Are you sure you want to delete this item?${communityOfPracticeToDelete.actualAttendeeCount != null ? ' This will also remove any tracking data associated with it.' : ''}`}
          onClose={() => { setCommunityOfPracticeToDelete(null) }}
          onConfirm={() => { void handleDeleteCommunityOfPractice(communityOfPracticeToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      }

      {showTrackingModal &&
        <AttendancePlanTrackingModal
          title={`${planBeingEdited?.startTraining.title ?? 'Event Attendance'} Tracking`}
          onSave={saveTrackingPlanBeingEdited}
          attendees={planBeingEdited?.actualAttendeeCount?.toString() ?? ''}
          budget={planBeingEdited?.actualDollarAmount ?? ''}
          onCancel={() => { setShowTrackingModal(false) }}
        />
      }
    </Box>
  </Box>
}
